// src/App.jsx
import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { useActiveMode } from './hooks/useActiveMode'
import { useOneSignalSync } from './hooks/useOneSignalSync'

// Públicas eager (entran rápido al primer render)
import Landing        from './pages/Landing'
import Login          from './pages/Login'
import Register       from './pages/Register'
import ResetPassword  from './pages/ResetPassword'
import Terms          from './pages/Terms'

// Cliente y proveedora se cargan bajo demanda (cada rol baja sólo lo suyo)
const NewRequest    = lazy(() => import('./pages/client/NewRequest'))
const MyRequests    = lazy(() => import('./pages/client/MyRequests'))
const ClientProfile = lazy(() => import('./pages/client/ClientProfile'))

const Feed       = lazy(() => import('./pages/provider/Feed'))
const LeadDetail = lazy(() => import('./pages/provider/LeadDetail'))
const MyLeads    = lazy(() => import('./pages/provider/MyLeads'))
const Profile    = lazy(() => import('./pages/provider/Profile'))
const Recargar   = lazy(() => import('./pages/provider/Recargar'))

// Admin se carga sólo si el usuario llega a /admin
const AdminPanel = lazy(() => import('./pages/admin/AdminPanel'))

import HamburgerMenu from './components/HamburgerMenu'
import ForegroundPushBanner from './components/ForegroundPushBanner'

const Spinner = () => (
  <div style={{
    minHeight: '100vh', backgroundColor: 'var(--bln-bg)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    <div style={{
      width: '36px', height: '36px',
      border: '3px solid var(--bln-border-strong)', borderTopColor: 'var(--bln-accent)',
      borderRadius: '50%', animation: 'spin 0.8s linear infinite'
    }}/>
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
  </div>
)

function App() {
  const { user, role, loading } = useAuth()
  const { activeMode } = useActiveMode(role)
  useOneSignalSync()
  const location = useLocation()

  // effectiveRole reemplaza a 'role' para enrutar — un provider en modo
  // cliente verá las rutas y navegación de cliente. Admin no usa modo.
  // Fallback a role si activeMode aún no se ha sincronizado (hay un tick
  // entre que useAuth setea role y useActiveMode lo refleja). Sin esto,
  // al crear cuenta el provider rebota a /login → spinner perpetuo en
  // iOS Safari.
  const effectiveRole = role === 'admin' ? 'admin' : (activeMode ?? role)

  const isPublic = ['/', '/login', '/registro', '/reset-password'].includes(location.pathname)
    || location.pathname.startsWith('/perfil/')

  if (loading && !isPublic) return <Spinner />

  // El HamburgerMenu agrupa todos los FABs (notif, soporte, logout, install,
  // toggle tema). Solo se muestra cuando hay sesión y fuera de páginas
  // públicas / perfil público.
  const showMenu =
    user
    && !['/login', '/registro', '/reset-password', '/terminos'].includes(location.pathname)
    && location.pathname !== '/'
    && !location.pathname.startsWith('/perfil/')

  return (
    <>
    <Suspense fallback={<Spinner />}>
    <Routes>
      <Route path="/" element={
        loading ? <Spinner /> :
        user && effectiveRole === 'client'   ? <Navigate to="/mis-solicitudes" replace /> :
        user && effectiveRole === 'provider' ? <Navigate to="/solicitudes" replace /> :
        user && effectiveRole === 'admin'    ? <Navigate to="/admin" replace /> :
        <Landing />
      } />
      <Route path="/login" element={
        user && effectiveRole === 'client'   ? <Navigate to="/mis-solicitudes" replace /> :
        user && effectiveRole === 'provider' ? <Navigate to="/solicitudes" replace /> :
        user && effectiveRole === 'admin'    ? <Navigate to="/admin" replace /> :
        <Login />
      } />
      <Route path="/registro" element={
        user && effectiveRole === 'client'   ? <Navigate to="/mis-solicitudes" replace /> :
        user && effectiveRole === 'provider' ? <Navigate to="/solicitudes" replace /> :
        user && effectiveRole === 'admin'    ? <Navigate to="/admin" replace /> :
        <Register />
      } />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terminos"       element={<Terms />} />
      <Route path="/perfil/:id"     element={<Profile />} />

      <Route path="/nueva-solicitud" element={
        loading ? <Spinner /> : user && effectiveRole === 'client' ? <NewRequest /> : <Navigate to="/login" replace />
      } />
      <Route path="/mis-solicitudes" element={
        loading ? <Spinner /> : user && effectiveRole === 'client' ? <MyRequests /> : <Navigate to="/login" replace />
      } />
      <Route path="/mi-cuenta" element={
        loading ? <Spinner /> : user && effectiveRole === 'client' ? <ClientProfile /> : <Navigate to="/login" replace />
      } />

      <Route path="/solicitudes" element={
        loading ? <Spinner /> : user && effectiveRole === 'provider' ? <Feed /> : <Navigate to="/login" replace />
      } />
      <Route path="/solicitudes/:id" element={
        loading ? <Spinner /> : user && effectiveRole === 'provider' ? <LeadDetail /> : <Navigate to="/login" replace />
      } />
      <Route path="/mis-leads" element={
        loading ? <Spinner /> : user && effectiveRole === 'provider' ? <MyLeads /> : <Navigate to="/login" replace />
      } />
      <Route path="/mi-perfil" element={
        loading ? <Spinner /> : user && effectiveRole === 'provider' ? <Profile /> : <Navigate to="/login" replace />
      } />
      <Route path="/recargar" element={
        loading ? <Spinner /> : user && effectiveRole === 'provider' ? <Recargar /> : <Navigate to="/login" replace />
      } />
      <Route path="/admin" element={
        loading ? <Spinner /> : user && effectiveRole === 'admin' ? <AdminPanel /> : <Navigate to="/login" replace />
      } />
    </Routes>
    </Suspense>
    {showMenu && <HamburgerMenu />}
    <ForegroundPushBanner />
    </>
  )
}

export default App